import { createReducer, on } from '@ngrx/store';
import { clientActions } from './client.actions';
import { ClientState, initialClientState } from './client.state';
import { ClientAuthErrorCode, ClientAuthorizationStatus } from './client.models';

export const clientFeatureKey = 'clientAuth';

export const clientReducer = createReducer(
  initialClientState,
  on(
    clientActions.setAuthorizationDeliveryMode,
    (state, { authorizationDeliveryMode }): ClientState => ({
      ...state,
      authorizationDeliveryMode
    })
  ),
  on(
    clientActions.setPendingProspectRegistration,
    (state, { registrationModel }): ClientState => ({
      ...state,
      pendingProspectRegistration: registrationModel
    })
  ),
  on(
    clientActions.setPendingProspectAdvertisementSource,
    (state, { advertisementSourceModel }): ClientState => ({
      ...state,
      pendingProspectAdvertisementSource: advertisementSourceModel
    })
  ),
  on(
    clientActions.clearPendingProspectData,
    (state): ClientState => ({
      ...state,
      pendingProspectRegistration: null,
      pendingProspectAdvertisementSource: null
    })
  ),
  on(
    clientActions.setClientType,
    (state, { clientType }): ClientState => ({
      ...state,
      clientType,
      authResponse: null,
      tokenResponse: null,
      requestCodeAuthError: null,
      validateCodeAuthError: null,
      authRequest: null,
      pendingProspectRegistration: null,
      pendingProspectAdvertisementSource: null,
      authorizedClientProfile: null,
      authorizationStatus: ClientAuthorizationStatus.Idle,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.restoreClientType,
    (state, { clientType }): ClientState => ({
      ...state,
      clientType
    })
  ),
  on(
    clientActions.cancelClientAuthentication,
    (state): ClientState => ({
      ...state,
      authResponse: null,
      tokenResponse: null,
      requestCodeAuthError: null,
      validateCodeAuthError: null,
      authRequest: null,
      authorizedClientProfile: null,
      authorizationStatus: ClientAuthorizationStatus.Idle,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.submitClientAuthentication,
    clientActions.submitProspectAuthentication,
    (state, { request }): ClientState => ({
      ...state,
      authResponse: state.authResponse,
      tokenResponse: null,
      requestCodeAuthError: null,
      validateCodeAuthError: null,
      authorizationDeliveryMode: request.deliveryMode || null,
      authRequest: request,
      authorizedClientProfile: null,
      authorizationStatus: ClientAuthorizationStatus.PendingOtp,
      isSubmittingAuthRequest: true,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.submitClientAuthenticationSuccess,
    (state, { authResponse }): ClientState => ({
      ...state,
      authResponse,
      requestCodeAuthError: null,
      isSubmittingAuthRequest: false
    })
  ),
  on(
    clientActions.submitClientAuthenticationFailure,
    (state, { authError }): ClientState => ({
      ...state,
      authResponse: state.authResponse,
      tokenResponse: null,
      requestCodeAuthError: authError,
      validateCodeAuthError: null,
      authorizedClientProfile: null,
      authorizationStatus:
        authError.errorCode === ClientAuthErrorCode.AccessBlocked
          ? ClientAuthorizationStatus.Blocked
          : state.authResponse
            ? ClientAuthorizationStatus.PendingOtp
            : ClientAuthorizationStatus.Idle,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.validateClientCode,
    (state): ClientState => ({
      ...state,
      tokenResponse: null,
      validateCodeAuthError: null,
      authorizedClientProfile: null,
      authorizationStatus: ClientAuthorizationStatus.Authorizing,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: true
    })
  ),
  on(
    clientActions.validateClientCodeSuccess,
    (state, { tokenResponse }): ClientState => ({
      ...state,
      tokenResponse,
      validateCodeAuthError: null
    })
  ),
  on(
    clientActions.validateClientCodeFailure,
    (state, { authError }): ClientState => ({
      ...state,
      tokenResponse: null,
      validateCodeAuthError: authError,
      authorizedClientProfile: null,
      authorizationStatus:
        authError.errorCode === ClientAuthErrorCode.AccessBlocked
          ? ClientAuthorizationStatus.Blocked
          : ClientAuthorizationStatus.PendingOtp,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.authorizeValidatedClient,
    (state): ClientState => ({
      ...state,
      validateCodeAuthError: null,
      authorizedClientProfile: null,
      authorizationStatus: ClientAuthorizationStatus.Authorizing,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: true
    })
  ),
  on(
    clientActions.authorizeValidatedClientSuccess,
    (state, { profile, status }): ClientState => ({
      ...state,
      authorizedClientProfile: profile,
      authorizationStatus: status,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.authorizeValidatedProspectSuccess,
    (state, { status }): ClientState => ({
      ...state,
      authorizedClientProfile: null,
      authorizationStatus: status,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(
    clientActions.authorizeValidatedClientFailure,
    (state, { status }): ClientState => ({
      ...state,
      authorizedClientProfile: null,
      authorizationStatus: status,
      isSubmittingAuthRequest: false,
      isAuthorizingClient: false
    })
  ),
  on(clientActions.reset, (): ClientState => initialClientState)
);
